import clsx from "clsx";
import React from "react";
import type { DatePickerProps } from "react-datepicker";
import { useField } from "react-final-form";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, FormHelperText, Theme } from "@mui/material";

import { DatePicker } from "./DatePicker.lazy";

// The stylesheet stays eager while the picker itself is code-split: react-datepicker sets
// `.react-datepicker-popper { z-index: 1 }`, and global.css raises it to 1100 at the same
// specificity so the calendar clears sticky table headers and the side panel. A chunk's
// stylesheet is appended to <head> when the chunk loads, i.e. after global.css, so shipping
// this import inside the lazy module hands the win back to the vendor rule.
import "react-datepicker/dist/react-datepicker.css";

const useStyles = makeStyles()((theme: Theme) => ({
    dateInputWrapper: {
        position: "relative",
        width: "100%",
    },
    calendarIcon: {
        position: "absolute",
        right: theme.spacing(3),
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
    },
    dateInput: {
        width: "100%",
        height: 40,
        padding: theme.spacing(1, 10, 1, 1.5),
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.spacing(1),
        fontSize: "1rem",
        boxSizing: "border-box",
        cursor: "pointer",
        "&:hover": {
            borderColor: theme.palette.grey[400],
        },
        "&:focus": {
            outline: "none",
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
        },
        "&:disabled": {
            cursor: "not-allowed",
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.text.disabled,
            "&:hover": {
                borderColor: theme.palette.grey[300],
            },
        },
    },
}));

type CustomDateInputProps = {
    value?: string;
    onClick?: React.MouseEventHandler<HTMLInputElement>;
    placeholder?: string;
    id?: string;
    disabled?: boolean;
};
const CustomDateInput = React.forwardRef<HTMLInputElement, CustomDateInputProps>((props, ref) => {
    const { classes } = useStyles();
    return (
        <Box className={classes.dateInputWrapper}>
            <input
                ref={ref}
                id={props.id}
                className={classes.dateInput}
                value={props.value}
                onClick={props.onClick}
                onChange={() => {}}
                placeholder={props.placeholder}
                disabled={props.disabled}
                readOnly
            />
            <i className={clsx(classes.calendarIcon, "fa-regular fa-calendar")} />
        </Box>
    );
});
CustomDateInput.displayName = "CustomDateInput";

const DatePickerLoadFailed = ({ id, placeholder }: { id?: string; placeholder?: string }) => {
    const { t } = useTranslation("common");

    return (
        <Box>
            <CustomDateInput id={id} placeholder={placeholder} disabled />
            <FormHelperText error>{t("error.failedToLoadFeature")}</FormHelperText>
        </Box>
    );
};

export function DateField({ placeholder, ...props }: DatePickerProps & { placeholder?: string }) {
    const { classes } = useStyles();
    return (
        <DatePicker
            portalId="date-picker-portal"
            placeholderText={placeholder}
            dateFormat="dd/MM/yyyy"
            showYearDropdown
            showMonthDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={10}
            wrapperClassName={classes.dateInputWrapper}
            customInput={<CustomDateInput />}
            loadingFallback={<CustomDateInput id={props.id} placeholder={placeholder} />}
            errorFallback={<DatePickerLoadFailed id={props.id} placeholder={placeholder} />}
            {...props}
        />
    );
}

export function ControlledDateField({
    name,
    onChange: _onChange,
    onCalendarClose: _onCalendarClose,
    ...props
}: DatePickerProps & { name: string; placeholder?: string }) {
    const { input, meta } = useField<Date>(name, { type: "date" });
    const error = meta.touched && meta.error;
    const selected = input.value || null;

    return (
        <Box>
            <DateField id={name} selected={selected} onChange={input.onChange} onCalendarClose={input.onBlur} {...props} />
            {error ? <FormHelperText error>{error}</FormHelperText> : null}
        </Box>
    );
}
